'use client';

/**
 * BlogListenButtonAuto — drop-in replacement for `<BlogListenButton>` that
 * auto-detects when audio narration becomes available after a background
 * generation job.
 *
 * Background: `/api/post/publish` flips status synchronously and kicks off
 * audio generation as a fire-and-forget background task. The MDX file gets
 * patched with `audioUrl` when the job finishes. But pages that were already
 * server-rendered (e.g. the editor preview or the public post page) won't
 * know about the new audioUrl unless someone polls or hard-refreshes.
 *
 * This wrapper handles that:
 *
 *   • If `audioUrl` is already present → render `<BlogListenButton>` normally.
 *   • If `audioUrl` is missing AND a generation job is in flight for this
 *     slug (checked via the shared localStorage flag that `PublishToggle`
 *     and `GenerateAudioButton` also use) → poll `/api/post/read` every 8s,
 *     swap in `<BlogListenButton>` the moment audio lands. Show a small
 *     "Audio is generating in background…" pill while polling so the
 *     reader knows what's happening.
 *   • If `audioUrl` is missing and no job is tracked → render nothing (the
 *     pre-existing behaviour — no misleading Web Speech duration pill).
 *
 * Polling caps at ~8 minutes worth of attempts and gives up cleanly (the
 * lib's own budget is 270s; extra slack covers retries + frontmatter write).
 */

import React, { useEffect, useRef, useState } from 'react';
import { BlogListenButton } from '@/components/blog-sections/BlogListenButton';

const LS_KEY = 'innovaccer-audio-generating';
const POLL_INTERVAL_MS = 8000;
const MAX_POLL_ATTEMPTS = 60; // 8s * 60 = 8 min ceiling
const RECORD_TTL_MS = 6 * 60 * 1000; // discard localStorage entries older than 6 min
// PublishToggle dispatches this when it kicks off an audio job. We listen
// so an already-mounted instance flips into the "generating…" pill +
// polling state without waiting for the user to navigate away and back.
const AUDIO_STARTED_EVENT = 'innovaccer:audio-generation-started';

interface GeneratingRecord {
  startedAt: number;
}

function isGenerating(slug: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return false;
    const map = JSON.parse(raw) as Record<string, GeneratingRecord>;
    const rec = map?.[slug];
    if (!rec || typeof rec.startedAt !== 'number') return false;
    return Date.now() - rec.startedAt < RECORD_TTL_MS;
  } catch {
    return false;
  }
}

function clearGenerating(slug: string) {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return;
    const map = JSON.parse(raw) as Record<string, GeneratingRecord>;
    if (!map?.[slug]) return;
    delete map[slug];
    window.localStorage.setItem(LS_KEY, JSON.stringify(map));
  } catch { /* non-fatal */ }
}

interface Props {
  slug: string;
  audioUrl?: string;
  audioTimings?: string;
  targetSelector?: string;
}

export function BlogListenButtonAuto({
  slug,
  audioUrl: initialUrl,
  audioTimings: initialTimings,
  targetSelector,
}: Props) {
  const [url, setUrl] = useState<string | undefined>(initialUrl);
  const [timings, setTimings] = useState<string | undefined>(initialTimings);
  const [polling, setPolling] = useState(false);
  // Bumped to force the polling effect to re-evaluate. Used when the
  // AUDIO_STARTED_EVENT fires for our slug — pre-existing instance needs
  // to flip into polling state immediately.
  const [pollTrigger, setPollTrigger] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Track mount state so we don't update React state after unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Sync local state with prop changes — important for the editor preview
  // where the parent might pass an updated audioUrl after a save.
  useEffect(() => {
    if (initialUrl && initialUrl !== url) {
      setUrl(initialUrl);
      setTimings(initialTimings);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl, initialTimings]);

  // Subscribe to the global "audio job started" push from PublishToggle.
  // When our slug fires, bump pollTrigger so the polling effect below
  // re-runs and starts the polling loop immediately. Without this, an
  // already-mounted instance stays idle until next mount.
  useEffect(() => {
    const onAudioStarted = (e: Event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (e as any).detail;
      if (!detail || detail.slug !== slug) return;
      if (!mountedRef.current) return;
      setPollTrigger((n) => n + 1);
    };
    window.addEventListener(AUDIO_STARTED_EVENT, onAudioStarted);
    return () => window.removeEventListener(AUDIO_STARTED_EVENT, onAudioStarted);
  }, [slug]);

  // Start polling if there's no audioUrl yet but we know a generation job
  // is in flight (localStorage flag set by PublishToggle / GenerateAudioButton).
  // Re-runs whenever pollTrigger bumps (i.e. the audio-started event fires).
  useEffect(() => {
    if (url) return; // already have audio
    if (!isGenerating(slug)) return; // no job tracked — nothing to poll for

    let attempts = 0;
    setPolling(true);

    const poll = async () => {
      if (!mountedRef.current) return;
      attempts++;
      try {
        const res = await fetch(`/api/post/read?slug=${encodeURIComponent(slug)}`, {
          cache: 'no-store',
        });
        if (res.ok) {
          const data = await res.json().catch(() => null);
          const newUrl = data?.meta?.audioUrl;
          const newTimings = data?.meta?.audioTimings;
          if (typeof newUrl === 'string' && newUrl.length > 0) {
            if (mountedRef.current) {
              setUrl(newUrl);
              setTimings(typeof newTimings === 'string' ? newTimings : undefined);
              setPolling(false);
            }
            clearGenerating(slug);
            return;
          }
        }
      } catch { /* network blip — keep polling */ }

      if (attempts < MAX_POLL_ATTEMPTS && mountedRef.current && isGenerating(slug)) {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } else {
        // Gave up — clear the flag so we don't keep retrying forever.
        if (mountedRef.current) setPolling(false);
        clearGenerating(slug);
      }
    };
    timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, url, pollTrigger]);

  if (url) {
    return <BlogListenButton audioSrc={url} audioTimings={timings} targetSelector={targetSelector} />;
  }

  if (polling) {
    return (
      <div
        className="blog-listen-pill"
        aria-live="polite"
        style={{ cursor: 'default', opacity: 0.85 }}
        title="Audio narration is generating in the background"
      >
        <span className="blog-listen-pill__icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'blogListenSpin 0.8s linear infinite' }}>
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" />
            <style>{`@keyframes blogListenSpin{to{transform:rotate(360deg)}}`}</style>
          </svg>
        </span>
        <span className="blog-listen-pill__label">Audio generating…</span>
      </div>
    );
  }

  // No audio URL and no generation job in flight — nothing to play.
  return null;
}
